import React, { createContext, type ReactNode, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../api/firebase/config';
import { getUser } from '../api/firebase/firestore/user';
import { UserSetType } from '../validation/user/userType';

type AuthContextType = {
  user: UserSetType | null;
  isLoading: boolean;
  setCurrentUser: (user: UserSetType | null) => void;
};

// デフォルト値にnoop関数を使用
const noop = (): void => void {};

export const AuthContext = createContext<AuthContextType>({
  user: null as UserSetType | null,
  isLoading: true,
  setCurrentUser: noop, // 空の関数の代わりにnoop関数を使用
});

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<UserSetType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async authUser => {
      if (authUser) {
        const user = await getUser(authUser.uid);
        if (user) {
          setUser({
            uid: authUser.uid,
            name: user.name,
            icon: user.iconUrl,
            role: user.role,
            daily_limit: user.daily_limit ?? null,
          });
          setIsLoading(false);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      } else {
        setUser(null);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const setCurrentUser = useCallback((newUser: UserSetType | null) => {
    setUser(newUser);
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, setCurrentUser }}>
      {children}
    </AuthContext.Provider>
  );
};
